import React, { useEffect, useMemo, useRef, useState } from 'react';

import api from '../../../api/axios';
import type { Course } from '../../../types/course';
import { materialAdminApi } from './api';
import type { MaterialSyncJob, SyncStatus } from './types';

type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'not_applicable';

interface CourseSyncRow {
  course: Course;
  driveStatus: StepStatus;
  driveError?: string;
  mobileStatus: StepStatus | SyncStatus;
  mobileError?: string;
  syncId?: string;
  progress: Record<string, number>;
}

const syncOptions = {
  maximumDepth: 20,
  maximumItems: 10000,
  includeUnsupported: true,
  publishNewMaterials: true,
};

const activeMobileStatuses = new Set<SyncStatus>(['queued', 'running', 'cancel_requested']);
const terminalMobileStatuses = new Set<SyncStatus>(['completed', 'partially_completed', 'failed', 'cancelled']);

const label = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

function badgeClass(status: string): string {
  if (status === 'completed') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'failed' || status === 'cancelled' || status === 'partially_completed') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'not_applicable') return 'border-slate-200 bg-slate-50 text-slate-500';
  return 'border-blue-200 bg-blue-50 text-blue-700';
}

function progressSummary(progress: Record<string, number>): string {
  const discovered = (progress.filesDiscovered || 0) + (progress.foldersDiscovered || 0);
  const changed = (progress.created || 0) + (progress.updated || 0) + (progress.reactivated || 0);
  return `${discovered} discovered · ${changed} updated · ${progress.deactivated || 0} removed`;
}

export default function BulkCourseMaterialSyncDialog({ courses, onClose }: { courses: Course[]; onClose: () => void }) {
  const started = useRef(false);
  const [rows, setRows] = useState<CourseSyncRow[]>(() => courses.map((course) => ({
    course,
    driveStatus: 'pending',
    mobileStatus: ['hybrid', 'mobile_app'].includes(course.contentDeliveryMode) ? 'pending' : 'not_applicable',
    progress: {},
  })));

  const update = (courseId: string, patch: Partial<CourseSyncRow>) => {
    setRows((current) => current.map((row) => row.course.id === courseId ? { ...row, ...patch } : row));
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const start = async () => {
      for (const course of courses) {
        update(course.id, { driveStatus: 'running' });
        try {
          await api.post('/courses/sync-drive', { courseId: course.id, driveFolderId: course.driveFolderId });
          update(course.id, { driveStatus: 'completed' });
        } catch (error: any) {
          update(course.id, { driveStatus: 'failed', driveError: error?.response?.data?.message || 'Drive synchronization failed.' });
        }

        if (!['hybrid', 'mobile_app'].includes(course.contentDeliveryMode)) continue;
        update(course.id, { mobileStatus: 'running' });
        try {
          const response = await materialAdminApi.createSync(course.id, syncOptions);
          update(course.id, { mobileStatus: response.data.status, syncId: response.data.id, progress: response.data.progress });
        } catch (error: any) {
          if (error?.code === 'MATERIAL_SYNC_ALREADY_ACTIVE' || error?.status === 409) {
            try {
              const existing = (await materialAdminApi.syncs(course.id)).data.items.find((job) => activeMobileStatuses.has(job.status));
              if (existing) {
                update(course.id, { mobileStatus: existing.status, syncId: existing.id, progress: existing.progress });
                continue;
              }
            } catch {
              // Fall through to the safe error shown below.
            }
          }
          update(course.id, { mobileStatus: 'failed', mobileError: 'Mobile catalogue synchronization could not be started.' });
        }
      }
    };

    void start();
  }, [courses]);

  useEffect(() => {
    const activeRows = rows.filter((row) => row.syncId && activeMobileStatuses.has(row.mobileStatus as SyncStatus));
    if (!activeRows.length) return;

    const timer = window.setTimeout(async () => {
      await Promise.all(activeRows.map(async (row) => {
        try {
          const response = await materialAdminApi.getSync(row.syncId!);
          const job: MaterialSyncJob = response.data;
          update(row.course.id, {
            mobileStatus: job.status,
            progress: job.progress,
            mobileError: terminalMobileStatuses.has(job.status) && job.status !== 'completed'
              ? job.lastErrorMessage || 'Mobile synchronization did not complete fully.'
              : undefined,
          });
        } catch {
          // A transient poll failure should not change the job result.
        }
      }));
    }, 2500);

    return () => window.clearTimeout(timer);
  }, [rows]);

  const summary = useMemo(() => {
    const complete = rows.filter((row) => row.driveStatus === 'completed' && (row.mobileStatus === 'completed' || row.mobileStatus === 'not_applicable')).length;
    const failed = rows.filter((row) => row.driveStatus === 'failed' || ['failed', 'cancelled', 'partially_completed'].includes(row.mobileStatus)).length;
    const running = rows.length - complete - failed;
    return { complete, failed, running };
  }, [rows]);

  return <div className="fixed inset-0 z-[1500] grid place-items-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="bulk-material-sync-title">
    <section className="flex max-h-[88vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
      <header className="flex items-start justify-between gap-4 border-b border-slate-100 p-6">
        <div>
          <p className="m-0 text-[11px] font-bold uppercase tracking-wider text-blue-600">Drive + mobile access</p>
          <h2 id="bulk-material-sync-title" className="mb-0 mt-1 text-xl">Synchronizing course materials</h2>
          <p className="mb-0 mt-1 text-sm text-slate-500">{summary.complete} completed · {summary.running} in progress · {summary.failed} need attention</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">{summary.running ? 'Hide' : 'Close'}</button>
      </header>
      <div className="overflow-y-auto p-6">
        <div className="grid gap-3">
          {rows.map((row) => <article key={row.course.id} className="rounded-xl border border-slate-200 p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <strong className="text-sm text-slate-900">{row.course.name}</strong>
              <span className="text-[11px] text-slate-400">{row.course.contentDeliveryMode.replace(/_/g, ' ')}</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-slate-600">Drive access</span><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${badgeClass(row.driveStatus)}`}>{label(row.driveStatus)}</span></div>
                {row.driveError && <p className="mb-0 mt-2 text-xs text-red-600">{row.driveError}</p>}
              </div>
              <div className="rounded-lg bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-slate-600">Mobile access</span><span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${badgeClass(row.mobileStatus)}`}>{label(row.mobileStatus)}</span></div>
                {row.syncId && <p className="mb-0 mt-2 text-[11px] text-slate-500">{progressSummary(row.progress)}</p>}
                {row.mobileStatus === 'not_applicable' && <p className="mb-0 mt-2 text-[11px] text-slate-500">Mobile delivery is not enabled for this course.</p>}
                {row.mobileError && <p className="mb-0 mt-2 text-xs text-red-600">{row.mobileError}</p>}
              </div>
            </div>
          </article>)}
        </div>
      </div>
    </section>
  </div>;
}
